import { useEffect } from "react";
import type { QuizListItem } from "../../../shared/types";
import { QuizMark, ATL_ANS } from "../../components/atelier";
import { Ico } from "../../components/shell";

/** Pick which quiz to take live. Shown from the dashboard's "Start live quiz"
    buttons when the host owns more than one quiz, so it never silently hosts a
    random one. Quizzes with no questions are listed but not selectable. */
export default function QuizPickerModal({
  quizzes,
  onPick,
  onClose,
}: {
  quizzes: QuizListItem[];
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 100, display: "grid", placeItems: "center", background: "rgba(40,25,15,.5)", padding: 24 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="atl-card"
        style={{ width: "min(560px, 96vw)", maxHeight: "86vh", overflowY: "auto", padding: 26 }}
      >
        <div className="atl-eyebrow atl-eyebrow--accent">START LIVE QUIZ</div>
        <h2 className="atl-serif" style={{ fontSize: 26, marginTop: 8 }}>Pick a quiz to host</h2>
        <p style={{ color: "var(--atl-soft)", fontSize: 14, fontWeight: 500, marginTop: 6 }}>
          Choose which bake goes live.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 18 }}>
          {quizzes.map((q, i) => {
            const empty = q.questionCount === 0;
            return (
              <button
                key={q.id}
                type="button"
                disabled={empty}
                onClick={() => onPick(q.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "11px 14px",
                  borderRadius: 14,
                  border: "1px solid var(--atl-line)",
                  background: "var(--atl-surface)",
                  cursor: empty ? "not-allowed" : "pointer",
                  opacity: empty ? 0.55 : 1,
                  textAlign: "left",
                  fontFamily: "var(--atl-sans)",
                  width: "100%",
                }}
              >
                <QuizMark c={ATL_ANS[i % ATL_ANS.length]} emoji={q.coverEmoji} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="atl-serif" style={{ fontSize: 16.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {q.title}
                  </div>
                  <div style={{ fontSize: 12.5, color: "var(--atl-faint)", fontWeight: 500, marginTop: 3 }}>
                    {q.questionCount} question{q.questionCount === 1 ? "" : "s"}
                    {empty ? " · add a question first" : ` · ${q.scoring} scoring`}
                  </div>
                </div>
                {!empty && <Ico name="play" size={15} />}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="atl-btn atl-btn--ghost"
          style={{ marginTop: 18 }}
          onClick={onClose}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
